/* =====================================================================
   Quick Add — bottom-sheet drawer for Symmetry (custom, self-contained)
   - Injects a circular icon button (no text) on collection product cards
   - Positioned top-right of product image
   - Fetches product JSON on demand (no pre-loading of variants)
   - Renders gallery + variant selectors + qty + add-to-cart in a
     bottom sheet that slides up on mobile AND desktop
   - Adds to cart via AJAX and refreshes/opens Symmetry's cart drawer
   - Focus trap, Esc-to-close, click-outside-to-close, scroll lock
   ===================================================================== */
(function () {
  'use strict';

  var CFG = window.quickAddConfig || {};
  var MONEY_FORMAT = CFG.moneyFormat || '${{amount}}';
  var HIDDEN = (CFG.hiddenOptionNames || ['color', 'צבע']).map(function (s) { return String(s).toLowerCase(); });
  var CART_ADD_URL = (window.theme && window.theme.routes && window.theme.routes.cartAdd) || '/cart/add.js';
  var S = CFG.strings || {};
  var T = {
    quickAdd: S.quickAdd || 'הוספה מהירה',
    addToCart: S.addToCart || 'הוספה לסל',
    soldOut: S.soldOut || 'אזל מהמלאי',
    choose: S.choose || 'בחר/י אפשרות',
    viewDetails: S.viewDetails || 'לעמוד המוצר המלא',
    error: S.error || 'אופס, משהו השתבש. נסו שוב.'
  };

  /* ---------- helpers ---------- */
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  function imgUrl(src, w) {
    if (!src) return '';
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + (w || 600);
  }
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var fmt = format || '${{amount}}';
    var re = /\{\{\s*(\w+)\s*\}\}/;
    function fwd(number, precision, thousands, decimal) {
      precision = isNaN(precision) ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var c = parts[1] ? decimal + parts[1] : '';
      return dollars + c;
    }
    var match = fmt.match(re);
    if (!match) return fmt;
    var value;
    switch (match[1]) {
      case 'amount': value = fwd(cents, 2); break;
      case 'amount_no_decimals': value = fwd(cents, 0); break;
      case 'amount_with_comma_separator': value = fwd(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = fwd(cents, 0, '.', ','); break;
      case 'amount_with_space_separator': value = fwd(cents, 2, ' ', '.'); break;
      case 'amount_no_decimals_with_space_separator': value = fwd(cents, 0, ' ', ''); break;
      case 'amount_with_apostrophe_separator': value = fwd(cents, 2, "'", '.'); break;
      default: value = fwd(cents, 2);
    }
    return fmt.replace(re, value);
  }

  /* ---------- trigger injection (collection pages, infinite-scroll safe) ---------- */
  /* Default = Symmetry basket icon (matches the theme's header cart icon).
     Overridden by quickAddConfig.triggerIcon rendered from snippet 'icon-basket'. */
  var DEFAULT_TRIGGER_ICON = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false"><g fill="none" fill-rule="evenodd" stroke="currentColor" stroke-width="1.5"><path d="M2 9.25h20l-4 12H6z"/><path stroke-linecap="square" d="M12 9V3"/></g></svg>';
  var TRIGGER_ICON = CFG.triggerIcon || DEFAULT_TRIGGER_ICON;
  var EXCLUDE = 'cart-drawer, .cart-drawer, predictive-search, .quickbuy-container, .qadd-sheet';

  function injectTriggers(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var blocks = scope.querySelectorAll('.product-block:not([data-qadd-done])');
    blocks.forEach(function (block) {
      block.setAttribute('data-qadd-done', '1');
      if (block.closest(EXCLUDE)) return;
      var cont = block.querySelector('.image-cont');
      var link = block.querySelector('a.product-link[href]');
      if (!cont || !link) return;
      var href = link.getAttribute('href') || '';
      var m = href.match(/\/products\/([^/?#]+)/);
      if (!m) return;
      var btn = el('button', 'qadd-trigger', TRIGGER_ICON);
      btn.type = 'button';
      btn.setAttribute('data-qadd-handle', m[1]);
      btn.setAttribute('data-qadd-url', href.split('?')[0]);
      btn.setAttribute('aria-label', T.quickAdd);
      btn.setAttribute('aria-haspopup', 'dialog');
      cont.appendChild(btn);
    });
  }

  /* ---------- drawer shell ---------- */
  var ui = {};
  var state = { product: null, optionNames: [], selected: {}, productUrl: '' };
  var lastFocused = null;

  function buildShell() {
    ui.overlay = el('div', 'qadd-overlay');
    ui.overlay.setAttribute('hidden', '');
    ui.sheet = el('div', 'qadd-sheet');
    ui.sheet.setAttribute('role', 'dialog');
    ui.sheet.setAttribute('aria-modal', 'true');
    ui.sheet.setAttribute('aria-label', T.quickAdd);

    var closeBtn = el('button', 'qadd-sheet__close', '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>');
    closeBtn.type = 'button';
    closeBtn.setAttribute('aria-label', 'סגירה');
    closeBtn.addEventListener('click', function () { close(); });

    ui.handle = el('div', 'qadd-sheet__handle');
    ui.body = el('div', 'qadd-sheet__body');
    ui.footer = el('div', 'qadd-footer');

    ui.sheet.appendChild(closeBtn);
    ui.sheet.appendChild(ui.handle);
    ui.sheet.appendChild(ui.body);
    ui.sheet.appendChild(ui.footer);
    ui.overlay.appendChild(ui.sheet);
    document.body.appendChild(ui.overlay);

    ui.overlay.addEventListener('click', function (e) { if (e.target === ui.overlay) close(); });
    document.addEventListener('keydown', onKeydown);
  }

  function onKeydown(e) {
    if (!ui.overlay.classList.contains('is-open')) return;
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key === 'Tab') trapFocus(e);
  }
  function focusables() {
    return Array.prototype.filter.call(
      ui.sheet.querySelectorAll('button, [href], input, select, [tabindex]:not([tabindex="-1"])'),
      function (n) { return !n.disabled && n.offsetParent !== null; }
    );
  }
  function trapFocus(e) {
    var f = focusables();
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ---------- open / close ---------- */
  function openSheet() {
    if (!ui.overlay) buildShell();
    lastFocused = document.activeElement;
    ui.overlay.removeAttribute('hidden');
    document.documentElement.classList.add('qadd-locked');
    requestAnimationFrame(function () { ui.overlay.classList.add('is-open'); });
  }
  function close() {
    if (!ui.overlay) return;
    ui.overlay.classList.remove('is-open');
    document.documentElement.classList.remove('qadd-locked');
    setTimeout(function () {
      if (!ui.overlay.classList.contains('is-open')) ui.overlay.setAttribute('hidden', '');
    }, 350);
    if (lastFocused && lastFocused.focus) { try { lastFocused.focus(); } catch (e) {} }
  }

  /* ---------- fetch + render product ---------- */
  function showLoading() {
    ui.body.innerHTML = '<div class="qadd-loading"><span class="qadd-spinner"></span></div>';
    ui.footer.innerHTML = '';
  }
  function loadProduct(handle, url) {
    state.productUrl = url || ('/products/' + handle);
    showLoading();
    fetch('/products/' + handle + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('fetch'); return r.json(); })
      .then(function (p) { renderProduct(p); })
      .catch(function () {
        ui.body.innerHTML = '<p class="qadd-loading">' + T.error + '</p>';
      });
  }

  function uniqueValues(variants, pos) {
    var seen = {}, out = [];
    variants.forEach(function (v) {
      var val = v['option' + pos];
      if (val != null && !seen[val]) { seen[val] = 1; out.push(val); }
    });
    return out;
  }
  function defaultVariant(p) {
    for (var i = 0; i < p.variants.length; i++) if (p.variants[i].available) return p.variants[i];
    return p.variants[0];
  }
  function currentVariant() {
    var p = state.product;
    if (!p) return null;
    for (var i = 0; i < p.variants.length; i++) {
      var v = p.variants[i], ok = true;
      for (var pos = 1; pos <= state.optionNames.length; pos++) {
        if (String(v['option' + pos]) !== String(state.selected[pos])) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }
  function valueAvailable(pos, value) {
    var p = state.product;
    for (var i = 0; i < p.variants.length; i++) {
      var v = p.variants[i];
      if (String(v['option' + pos]) !== String(value)) continue;
      var match = true;
      for (var o = 1; o <= state.optionNames.length; o++) {
        if (o === pos) continue;
        if (String(v['option' + o]) !== String(state.selected[o])) { match = false; break; }
      }
      if (match && v.available) return true;
    }
    return false;
  }

  function renderProduct(p) {
    state.product = p;
    state.optionNames = (p.options || []).map(function (o) { return typeof o === 'string' ? o : o.name; });
    state.selected = {};
    var def = defaultVariant(p);
    for (var pos = 1; pos <= state.optionNames.length; pos++) state.selected[pos] = def['option' + pos];

    ui.body.innerHTML = '';

    /* gallery */
    var media = (p.media || []).filter(function (m) { return m.media_type === 'image'; });
    var imgs = media.length ? media.map(function (m) { return { src: m.src, w: m.width, h: m.height }; })
      : (p.images || []).map(function (s) { return { src: s }; });
    if (imgs.length) {
      var ar = (imgs[0].w && imgs[0].h) ? (imgs[0].w + ' / ' + imgs[0].h) : '3 / 4';
      var gal = el('div', 'qadd-gallery');
      gal.style.setProperty('--qadd-ar', ar);
      imgs.forEach(function (im) {
        var i = el('img');
        i.src = imgUrl(im.src, 600);
        i.alt = p.title;
        i.loading = 'lazy';
        i.setAttribute('data-src-base', im.src);
        gal.appendChild(i);
      });
      ui.gallery = gal;
      ui.body.appendChild(gal);
    } else { ui.gallery = null; }

    ui.body.appendChild(el('h2', 'qadd-title', p.title));
    ui.price = el('div', 'qadd-price');
    ui.body.appendChild(ui.price);

    /* options (skip hidden ones, e.g. Color) */
    state.optionNames.forEach(function (name, idx) {
      var pos = idx + 1;
      if (HIDDEN.indexOf(String(name).toLowerCase()) > -1) return;
      var values = uniqueValues(p.variants, pos);
      if (values.length <= 1 && name === 'Title') return;
      var wrap = el('div', 'qadd-option');
      wrap.appendChild(el('span', 'qadd-option__name', name));
      var row = el('div', 'qadd-option__values');
      values.forEach(function (val) {
        var b = el('button', 'qadd-swatch', '<span>' + val + '</span>');
        b.type = 'button';
        b.setAttribute('data-pos', pos);
        b.setAttribute('data-val', val);
        b.addEventListener('click', function () {
          state.selected[pos] = val;
          syncOptions();
          updateForVariant();
        });
        row.appendChild(b);
      });
      wrap.appendChild(row);
      ui.body.appendChild(wrap);
    });

    /* quantity */
    var qtyWrap = el('div', 'qadd-option');
    qtyWrap.appendChild(el('span', 'qadd-option__name', 'כמות'));
    var qty = el('div', 'qadd-qty');
    var minus = el('button', null, '−'); minus.type = 'button'; minus.setAttribute('aria-label', 'הפחתת כמות');
    var input = el('input'); input.type = 'number'; input.min = '1'; input.value = '1'; input.inputMode = 'numeric';
    var plus = el('button', null, '+'); plus.type = 'button'; plus.setAttribute('aria-label', 'הוספת כמות');
    minus.addEventListener('click', function () { input.value = Math.max(1, (parseInt(input.value, 10) || 1) - 1); });
    plus.addEventListener('click', function () { input.value = (parseInt(input.value, 10) || 1) + 1; });
    input.addEventListener('change', function () { if ((parseInt(input.value, 10) || 0) < 1) input.value = '1'; });
    qty.appendChild(minus); qty.appendChild(input); qty.appendChild(plus);
    qtyWrap.appendChild(qty);
    ui.qtyInput = input;
    ui.body.appendChild(qtyWrap);

    var fullLink = el('a', 'qadd-fulllink', T.viewDetails);
    fullLink.href = state.productUrl;
    ui.body.appendChild(fullLink);

    /* footer */
    ui.footer.innerHTML = '';
    ui.error = el('div', 'qadd-error');
    ui.addBtn = el('button', 'btn btn--large btn--wide', T.addToCart);
    ui.addBtn.type = 'button';
    ui.addBtn.addEventListener('click', addToCart);
    ui.footer.appendChild(ui.error);
    ui.footer.appendChild(ui.addBtn);

    syncOptions();
    updateForVariant();

    var f = focusables();
    if (f.length) f[0].focus();
  }

  function syncOptions() {
    ui.body.querySelectorAll('.qadd-swatch').forEach(function (b) {
      var pos = parseInt(b.getAttribute('data-pos'), 10);
      var val = b.getAttribute('data-val');
      b.setAttribute('aria-pressed', String(state.selected[pos]) === String(val) ? 'true' : 'false');
      b.disabled = !valueAvailable(pos, val);
    });
  }

  function updateForVariant() {
    var v = currentVariant();
    if (v) {
      var html = '';
      if (v.compare_at_price && v.compare_at_price > v.price) {
        html = '<del>' + formatMoney(v.compare_at_price, MONEY_FORMAT) + '</del>' +
          '<span class="qadd-price__sale">' + formatMoney(v.price, MONEY_FORMAT) + '</span>';
      } else {
        html = formatMoney(v.price, MONEY_FORMAT);
      }
      ui.price.innerHTML = html;
    } else if (state.product) {
      ui.price.innerHTML = formatMoney(state.product.price, MONEY_FORMAT);
    }
    if (v && v.featured_image && v.featured_image.src && ui.gallery) {
      var base = v.featured_image.src.split('?')[0];
      var target = Array.prototype.find.call(ui.gallery.querySelectorAll('img'), function (im) {
        return (im.getAttribute('data-src-base') || '').split('?')[0] === base;
      });
      if (target) ui.gallery.scrollLeft = target.offsetLeft - ui.gallery.offsetLeft;
    }
    if (!v) { ui.addBtn.disabled = true; ui.addBtn.textContent = T.choose; }
    else if (!v.available) { ui.addBtn.disabled = true; ui.addBtn.textContent = T.soldOut; }
    else { ui.addBtn.disabled = false; ui.addBtn.textContent = T.addToCart; }
  }

  /* ---------- add to cart ---------- */
  function addToCart() {
    var v = currentVariant();
    if (!v || !v.available) return;
    var qty = parseInt(ui.qtyInput && ui.qtyInput.value, 10) || 1;
    ui.error.classList.remove('is-visible');
    ui.addBtn.disabled = true;
    var prev = ui.addBtn.textContent;
    ui.addBtn.textContent = '...';
    fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [{ id: v.id, quantity: qty }] })
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (res) {
        if (!res.ok) {
          ui.error.textContent = (res.data && (res.data.description || res.data.message)) || T.error;
          ui.error.classList.add('is-visible');
          ui.addBtn.disabled = false;
          ui.addBtn.textContent = prev;
          return;
        }
        refreshThemeCart();
        close();
      })
      .catch(function () {
        ui.error.textContent = T.error;
        ui.error.classList.add('is-visible');
        ui.addBtn.disabled = false;
        ui.addBtn.textContent = prev;
      });
  }

  function refreshThemeCart() {
    var cartDrawer = document.querySelector('cart-drawer');
    var cartForm = document.querySelector('cart-drawer cart-form');
    if (cartForm && typeof cartForm.refresh === 'function') {
      try { cartForm.refresh(); } catch (e) {}
    }
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
    if (cartDrawer) {
      setTimeout(function () {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
      }, 360);
    } else {
      window.location.href = (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
    }
  }

  /* ---------- wire up ---------- */
  function onClick(e) {
    var trigger = e.target.closest && e.target.closest('.qadd-trigger');
    if (!trigger) return;
    e.preventDefault();
    e.stopPropagation();
    openSheet();
    loadProduct(trigger.getAttribute('data-qadd-handle'), trigger.getAttribute('data-qadd-url'));
  }

  function init() {
    if (document.body.classList.contains('template-collection')) {
      injectTriggers(document);
      var pending = null;
      var mo = new MutationObserver(function () {
        clearTimeout(pending);
        pending = setTimeout(function () { injectTriggers(document); }, 150);
      });
      mo.observe(document.body, { childList: true, subtree: true });
    }
    document.addEventListener('click', onClick, true);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
